/**
 * BETWEEN-SALES HOME PAGE — the content. Shown on `/` whenever no public sale is live or closing
 * (Home.tsx decides; BetweenSales.tsx renders). Edit this file and redeploy hosting to change it.
 *
 * SELF-EXPIRING, so it cannot go stale the way the old SiteNotice banner did:
 *   - an event disappears on its own once its `endDate` has passed (end of that day, Eastern);
 *   - a coming-soon site with `showUntil` disappears after that date.
 * Nothing here is a count. Dates are the only facts, and each one retires its own entry.
 *
 * Preview while a sale is live:  /?between=1
 */
export interface UpcomingEvent {
  id: string;                  // stable key, e.g. '2026-11-estate-a'
  kind: 'estate-sale' | 'auction' | 'event';
  title: string;               // "MM-YYYY Description"
  startDate?: string;          // 'YYYY-MM-DD' (Eastern) — omit when only the closing day is set
  endDate: string;             // 'YYYY-MM-DD' (Eastern) — the entry hides after this day
  dateLabel?: string;          // shown INSTEAD of the computed dates, e.g. "November 2026" while dates are unset
  hours?: string;              // free text, e.g. "Fri–Sat 8 AM – 3 PM"
  venue?: string;              // "City, ST" — gets a map link
  address?: string;            // full street address, shown only if you want it public
  description?: string;        // the event's own copy
  image?: string;              // URL of a hero photo
  link?: { label: string; url: string };   // e.g. the catalog / preview / registration page
}

export interface ComingSoonSite {
  name: string;                // e.g. "schmidt.skupoker.com"
  tagline: string;             // one line, no dates you don't mean to keep
  url?: string;                // omit to show it without a link
  showUntil?: string;          // 'YYYY-MM-DD' — optional self-retire
}

export interface Teaser {
  heading: string;
  text: string;
  showUntil?: string;          // 'YYYY-MM-DD' — optional self-retire
}

/** The market boards (Schmidt-Market). One home for the URL: PublicAuctionList's OPEN SLOT card imports it too. */
export const MARKET_SITE_URL = 'https://schmidt-market.web.app/market-board';

export const BETWEEN_SALES = {
  heading: 'Coming up at Schmidt Auctions',
  intro: 'Here is what is next on the calendar.',
  events: [
    {
      id: '2026-11-multi-estate',
      kind: 'estate-sale',
      title: '11-2026 Multi-Estate Sale',
      endDate: '2026-11-15',
      description: 'Several estates in one sale. Catalog coming soon.',
    },
    {
      id: '2026-11-nfl-executive-part-1',
      kind: 'estate-sale',
      title: '11-2026 Retired NFL Front Office Executive Collection — Part 1 of 3',
      // Closing date not set yet: shows "November 2026" and retires itself after 30 November.
      // When the date is set, replace dateLabel with startDate/endDate.
      endDate: '2026-11-30',
      dateLabel: 'November 2026',
      description: 'The first of three auctions from a retired NFL Front Office Executive with 30+ years of experience working for the league, as he begins downsizing his collection.',
    },
  ] as UpcomingEvent[],
  comingSoonHeading: 'Coming soon',
  comingSoon: [] as ComingSoonSite[],
  teaser: {
    heading: 'More is on the way',
    text: 'Several new sites are in the works. Watch this space.',
  } as Teaser | null,
  nowBuying: {
    headline: 'NOW BUYING',
    text: 'Our market boards post what we are buying right now and the price we pay.',
    linkLabel: 'See the market boards',
    url: MARKET_SITE_URL,
  },
  phone: '484-366-1780',
  email: 'dan@schmidtauctions.com',
};

/** True while `ymd` (Eastern) is today or later. Unparseable dates count as expired — fail closed. */
export const notPast = (ymd: string | undefined, now = new Date()): boolean => {
  if (!ymd) return true;
  const t = Date.parse(`${ymd}T23:59:59-05:00`);
  return Number.isFinite(t) && now.getTime() <= t;
};
